import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { getUserRoleLabel } from '../types';
import type { UserRole } from '../types';
import { Calendar, Users, FileText, Clock, DollarSign, Building2, TrendingUp, ArrowLeft, Settings2, X, Eye, EyeOff } from 'lucide-react';
import { api } from '../services/api';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

type WidgetId = 'summary' | 'billing' | 'trend' | 'recentLogs';

const WIDGET_DEFS: Record<WidgetId, { roles: UserRole[] }> = {
  summary: { roles: ['admin', 'agent', 'customer_manager', 'customer_viewer'] },
  billing: { roles: ['admin', 'customer_manager'] },
  trend: { roles: ['admin', 'agent'] },
  recentLogs: { roles: ['admin', 'agent'] },
};

function SummaryCards({ summary, isAdmin, isManager, t }: any) {
  const totalHours = summary ? (summary.hours.total_minutes / 60).toFixed(1) : '0';
  const billableHours = summary ? (summary.hours.billable_minutes / 60).toFixed(1) : '0';

  return (
    <div className={`grid grid-cols-2 gap-4 ${isAdmin ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
      <div className="card">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary-100 rounded-lg">
            <FileText className="text-primary-600" size={24} />
          </div>
          <div>
            <p className="text-surface-600 text-sm">{t('dashboard.activeTickets')}</p>
            <p className="text-2xl font-bold text-surface-900">{summary?.tickets.active ?? 0}</p>
            <p className="text-xs text-surface-400">{`${t('dashboard.outOf')}${summary?.tickets.total ?? 0}`}</p>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-success-100 rounded-lg">
            <Clock className="text-success-600" size={24} />
          </div>
          <div>
            <p className="text-surface-600 text-sm">{t('dashboard.monthlyHours')}</p>
            <p className="text-2xl font-bold text-surface-900">{totalHours}</p>
            <p className="text-xs text-surface-400">{`${t('dashboard.billableHours')}${billableHours}`}</p>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <Users className="text-purple-600" size={24} />
          </div>
          <div>
            <p className="text-surface-600 text-sm">{t('dashboard.activeUsers')}</p>
            <p className="text-2xl font-bold text-surface-900">{summary?.users ?? 0}</p>
          </div>
        </div>
      </div>
      {isAdmin && (
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-info-100 rounded-lg">
              <Building2 className="text-info-600" size={24} />
            </div>
            <div>
              <p className="text-surface-600 text-sm">{t('dashboard.organizations')}</p>
              <p className="text-2xl font-bold text-surface-900">{summary?.organizations ?? 0}</p>
            </div>
          </div>
        </div>
      )}
      {(isAdmin || isManager) && (
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-warning-100 rounded-lg">
              <DollarSign className="text-warning-600" size={24} />
            </div>
            <div>
              <p className="text-surface-600 text-sm">{t('dashboard.monthlyRevenue')}</p>
              <p className="text-2xl font-bold text-surface-900">
                {(summary?.monthly_revenue ?? 0).toLocaleString('he-IL')} &#8362;
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BillingWidget({ billing, t }: any) {
  if (!billing || billing.length === 0) return null;
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-surface-900">{t('dashboard.monthlySummary')}</h2>
        <Link to="/billing" className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1">
          {t('common.viewAll')} <ArrowLeft size={14} />
        </Link>
      </div>
      <div className="space-y-3">
        {billing.slice(0, 5).map((b: any) => (
          <div key={b.organization_id} className="flex items-center justify-between py-2 border-b last:border-0">
            <div>
              <p className="font-medium text-surface-900">{b.organization_name}</p>
              <p className="text-sm text-surface-500">
                {`${parseFloat(String(b.billable_hours)).toFixed(1)}${t('dashboard.billableHoursShort')}\u2022 ${b.total_logs}${t('dashboard.recordCount')}`}
              </p>
            </div>
            <div className="text-left">
              <p className="font-bold text-surface-900">
                {parseFloat(String(b.total_amount)).toLocaleString('he-IL', { maximumFractionDigits: 0 })} &#8362;
              </p>
              <p className="text-xs text-surface-400">{`${b.hourly_rate} &#8362;${t('dashboard.perHour')}`}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendWidget({ trend, t }: any) {
  if (!trend || trend.length === 0) return null;
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={20} className="text-primary-600" />
        <h2 className="text-xl font-bold text-surface-900">{t('dashboard.monthlyTrend')}</h2>
      </div>
      <div className="space-y-3">
        {trend.map((row: any) => {
          const totalH = parseFloat(String(row.total_hours));
          const billH = parseFloat(String(row.billable_hours));
          const maxH = Math.max(...trend.map((x: any) => parseFloat(String(x.total_hours))), 1);
          return (
            <div key={row.month} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-surface-600">
                  {format(new Date(row.month), 'MMMM yyyy', { locale: he })}
                </span>
                <span className="text-surface-900 font-medium">{`${totalH.toFixed(1)} ${t('common.hours')}`}</span>
              </div>
              <div className="w-full bg-surface-100 rounded-full h-3">
                <div
                  className="bg-primary-500 h-3 rounded-full transition-all relative"
                  style={{ width: `${(totalH / maxH) * 100}%` }}
                >
                  <div
                    className="absolute inset-y-0 right-0 bg-primary-700 rounded-full"
                    style={{ width: totalH > 0 ? `${(billH / totalH) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            </div>
          );
        })}
        <div className="flex items-center gap-4 text-xs text-surface-500 pt-2">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-primary-500 rounded-full inline-block" /> {t('common.total')}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-primary-700 rounded-full inline-block" /> {t('common.billable')}</span>
        </div>
      </div>
    </div>
  );
}

function RecentLogsWidget({ logs, t }: any) {
  if (!logs || logs.length === 0) return null;
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-surface-900">{t('dashboard.recentActivity')}</h2>
        <Link to="/work-logs" className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1">
          {t('common.viewAll')} <ArrowLeft size={14} />
        </Link>
      </div>
      <div className="space-y-3">
        {logs.map((log: any) => (
          <div key={log.id} className="flex items-center justify-between py-2 border-b last:border-0">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${log.source === 'voice_ai' ? 'bg-purple-100' : 'bg-info-100'}`}>
                <Clock className={log.source === 'voice_ai' ? 'text-purple-600' : 'text-info-600'} size={16} />
              </div>
              <div>
                <p className="font-medium text-surface-900 text-sm">{log.ticket_title}</p>
                <p className="text-xs text-surface-500">{log.agent_name} {log.organization_name ? `• ${log.organization_name}` : ''}</p>
              </div>
            </div>
            <div className="text-left">
              <p className="font-medium text-primary-600 text-sm">{`${log.duration_minutes} ${t('common.minutes')}`}</p>
              <p className="text-xs text-surface-400">
                {format(new Date(log.created_at), 'dd/MM HH:mm', { locale: he })}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomizeModal({
  widgets,
  hiddenWidgets,
  onToggle,
  onReset,
  onClose,
  t,
}: {
  widgets: { id: WidgetId; label: string }[];
  hiddenWidgets: string[];
  onToggle: (id: string) => void;
  onReset: () => void;
  onClose: () => void;
  t: any;
}) {
  return (
    <div className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content max-w-sm w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-surface-900">{t('dashboard.customize')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          {widgets.map((w) => {
            const isHidden = hiddenWidgets.includes(w.id);
            return (
              <label key={w.id} className="flex items-center justify-between py-2 cursor-pointer">
                <span className="text-sm text-surface-700">{w.label}</span>
                <button
                  onClick={() => onToggle(w.id)}
                  className={`p-1.5 rounded-lg transition-colors ${isHidden ? 'text-surface-400 hover:text-surface-600' : 'text-primary-600 hover:text-primary-700'}`}
                >
                  {isHidden ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </label>
            );
          })}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onReset} className="btn bg-surface-100 text-surface-600 text-sm flex-1">{t('dashboard.resetDefaults')}</button>
          <button onClick={onClose} className="btn btn-primary text-sm flex-1">{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
}

const WIDGET_LABELS: Record<WidgetId, string> = {
  summary: 'dashboard.summaryWidget',
  billing: 'dashboard.billingWidget',
  trend: 'dashboard.trendWidget',
  recentLogs: 'dashboard.recentLogsWidget',
};

export default function DashboardPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user, organization } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'customer_manager';
  const [showCustomize, setShowCustomize] = useState(false);

  const { data: profileData } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
  });

  const hiddenWidgets: string[] = profileData?.data?.dashboard_widget_preferences ?? [];

  const savePrefsMutation = useMutation({
    mutationFn: (prefs: string[]) => api.updateDashboardPreferences(prefs),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  });

  const userRole = (user?.role || 'customer_viewer') as UserRole;
  const availableWidgets = (Object.entries(WIDGET_DEFS) as [WidgetId, { roles: UserRole[] }][])
    .filter(([, def]) => def.roles.includes(userRole))
    .map(([id]) => id);

  const isWidgetVisible = (id: WidgetId) =>
    availableWidgets.includes(id) && !hiddenWidgets.includes(id);

  const toggleWidget = (id: string) => {
    const newPrefs = hiddenWidgets.includes(id)
      ? hiddenWidgets.filter((w) => w !== id)
      : [...hiddenWidgets, id];
    savePrefsMutation.mutate(newPrefs);
  };

  const resetDefaults = () => {
    savePrefsMutation.mutate([]);
  };

  const { data: summaryData } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => api.getDashboardSummary(),
    enabled: isWidgetVisible('summary'),
  });

  const { data: billingData } = useQuery({
    queryKey: ['billing'],
    queryFn: () => api.getBilling(),
    enabled: isWidgetVisible('billing'),
  });

  const { data: trendData } = useQuery({
    queryKey: ['monthly-trend'],
    queryFn: () => api.getMonthlyTrend(),
    enabled: isWidgetVisible('trend'),
  });

  const summary = summaryData?.data;
  const billing = billingData?.data ?? [];
  const trend = trendData?.data ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="page-title">
            {`${t('dashboard.greeting')}${user?.name ?? ''}`}
          </h1>
          <p className="text-surface-600 mt-1">
            {organization?.name} &bull; {user && getUserRoleLabel(user.role, t)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCustomize(true)}
            className="flex items-center gap-1 text-sm text-surface-500 hover:text-primary-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-surface-100"
          >
            <Settings2 size={16} /> {t('dashboard.customize')}
          </button>
          <div className="flex items-center gap-2 text-surface-600">
            <Calendar size={20} />
            <span className="hidden md:inline">{new Date().toLocaleDateString('he-IL', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            })}</span>
          </div>
        </div>
      </div>

      {isWidgetVisible('summary') && (
        <SummaryCards summary={summary} isAdmin={isAdmin} isManager={isManager} t={t} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {isWidgetVisible('billing') && <BillingWidget billing={billing} t={t} />}
        {isWidgetVisible('trend') && <TrendWidget trend={trend} t={t} />}
      </div>

      {isWidgetVisible('recentLogs') && (
        <RecentLogsWidget logs={summary?.recent_work_logs} t={t} />
      )}

      {showCustomize && (
        <CustomizeModal
          widgets={availableWidgets.map((id) => ({ id, label: t(WIDGET_LABELS[id]) }))}
          hiddenWidgets={hiddenWidgets}
          onToggle={toggleWidget}
          onReset={resetDefaults}
          onClose={() => setShowCustomize(false)}
          t={t}
        />
      )}
    </div>
  );
}
